/**
 * 運営画面の設定フォームの下書きと、設定更新リクエストの相互変換。
 *
 * 投票者・運営はメールを改行・カンマ区切りのテキストで編集し、送るときに配列へ直す。
 * 形式・重複・存在しない部門などの検証は API（domain の cuttysark-rules）に任せる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの設定フォーム下書きと更新リクエストの変換
 * @graph-connects none
 */

import type {
  CuttysarkDivision,
  CuttysarkLiveView,
  UpdateCuttysarkSessionRequest,
} from '@cortex/domain-product-innovation-lab-types';
import { parseEmailList } from './cuttysark-helpers.js';

/** 候補者行の下書き。 @graph-connects none */
export interface CandidateRowDraft {
  email: string;
  divisionId: string;
  title: string;
  intro: string;
  materialUrl: string;
  photoUrl: string;
}

/** 設定フォームの下書き。 @graph-connects none */
export interface SetupDraft {
  title: string;
  /** Google Meet の URL（空なら未設定）。 */
  meetUrl: string;
  divisions: CuttysarkDivision[];
  candidates: CandidateRowDraft[];
  voterMode: 'listed' | 'anyone';
  votersText: string;
  operatorsText: string;
}

/** 部門を足すときの初期値（1 人 2 票・2 人勝ち上がり）。 @graph-connects none */
export const NEW_DIVISION: CuttysarkDivision = {
  id: '',
  name: '',
  votesPerVoter: 2,
  advanceCount: 2,
};

/** 候補者を足すときの初期値。部門は先頭の部門にしておく。 @graph-connects none */
export function newCandidateRow(divisions: readonly CuttysarkDivision[]): CandidateRowDraft {
  return {
    email: '',
    divisionId: divisions[0]?.id ?? '',
    title: '',
    intro: '',
    materialUrl: '',
    photoUrl: '',
  };
}

/**
 * いまの設定から下書きを作る（運営向けの情報が無い閲覧者には呼ばない）。
 * @graph-connects none
 */
export function setupDraftFromView(view: CuttysarkLiveView): SetupDraft {
  return {
    title: view.title,
    meetUrl: view.meetUrl,
    divisions: view.divisions.map((d) => ({ ...d })),
    candidates: view.candidates.map((c) => ({
      email: c.email,
      divisionId: c.divisionId,
      title: c.title,
      intro: c.intro,
      materialUrl: c.materialUrl,
      photoUrl: c.photoUrl,
    })),
    voterMode: view.operatorView?.voterMode ?? 'listed',
    votersText: (view.operatorView?.voters ?? []).join('\n'),
    operatorsText: (view.operatorView?.operators ?? []).join('\n'),
  };
}

/**
 * 下書きを設定更新リクエストにする。メールが空の候補者行は送らない（足しかけの行を無視する）。
 * @graph-connects parseEmailList [calls] メール一覧の読み取り
 */
export function setupRequestFromDraft(
  draft: SetupDraft,
  baseRevision: number,
): UpdateCuttysarkSessionRequest {
  return {
    baseRevision,
    title: draft.title,
    meetUrl: draft.meetUrl.trim(),
    divisions: draft.divisions,
    candidates: draft.candidates
      .filter((c) => c.email.trim() !== '')
      .map((c) => ({ ...c, email: c.email.trim().toLowerCase() })),
    voterMode: draft.voterMode,
    voters: parseEmailList(draft.votersText),
    operators: parseEmailList(draft.operatorsText),
  };
}

/**
 * 数値入力を整数にする。空や数値でないものは 0（範囲外も含め API の検証で直してもらう）。
 * @graph-connects none
 */
export function parseIntegerInput(value: string): number {
  const n = Number(value);
  return Number.isInteger(n) ? n : 0;
}
